
import { useEffect } from "react";
import { useSocialCore } from "./social/useSocialCore";
import { useSocialLikes } from "./social/useSocialLikes";
import { useSocialFavorites } from "./social/useSocialFavorites";
import { useSocialComments } from "./social/useSocialComments";

/**
 * Main social features hook that combines core functionality with specific social features
 */
export const useSocial = (
  derivativeId: string,
  initialCounts?: {
    likes_count?: number;
    favorites_count?: number;
    comments_count?: number;
  }
) => {
  // Get core social functionality
  const core = useSocialCore(derivativeId, initialCounts);
  const { isAuthenticated, isAcademy, updateCount, canComment } = core;
  
  // Get likes functionality
  const { isLiked, toggleLike } = useSocialLikes(
    derivativeId, 
    isAuthenticated, 
    updateCount
  );
  
  // Get favorites functionality
  const { isFavorited, toggleFavorite } = useSocialFavorites(
    derivativeId, 
    isAuthenticated, 
    updateCount
  );
  
  // Get comments functionality
  const { 
    comments, 
    isCommentLoading, 
    fetchComments, 
    addComment, 
    updateComment, 
    deleteComment, 
    commentsLoaded 
  } = useSocialComments(
    derivativeId, 
    isAuthenticated, 
    isAcademy, 
    updateCount
  );
  
  // Automatically fetch comments when the component using this hook mounts
  useEffect(() => {
    if (derivativeId && !commentsLoaded) {
      fetchComments();
    }
  }, [derivativeId, fetchComments, commentsLoaded]);
  
  // Return all functionality in a single object
  return {
    ...core,
    isLiked,
    isFavorited,
    toggleLike,
    toggleFavorite,
    comments,
    isCommentLoading,
    addComment,
    updateComment,
    deleteComment,
    fetchComments,
    commentsLoaded
  };
};
